import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  CheckCircle, Clock, Plus, PlayCircle, SkipForward, ClockCountdown,
  Warning, ArrowsClockwise, Pause, Trash, X, ClipboardText, Phone,
  User as UserIcon, Clock as ClockIcon, ListChecks, EnvelopeSimple,
} from "@phosphor-icons/react";

const FREQUENCIES = [
  { value: "once", label: "One-off (due date)" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (every 3 months)" },
  { value: "yearly", label: "Yearly" },
  { value: "fy_quarterly", label: "FY Quarterly (Apr–Mar)" },
];

const CATEGORIES = [
  { value: "compliance", label: "Compliance", color: "bg-violet-100 text-violet-800" },
  { value: "hr", label: "HR", color: "bg-blue-100 text-blue-800" },
  { value: "accounts", label: "Accounts", color: "bg-emerald-100 text-emerald-800" },
  { value: "inventory", label: "Inventory", color: "bg-amber-100 text-amber-800" },
  { value: "sales", label: "Sales", color: "bg-rose-100 text-rose-800" },
  { value: "service", label: "Service", color: "bg-cyan-100 text-cyan-800" },
  { value: "other", label: "Other", color: "bg-neutral-100 text-neutral-700" },
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function categoryMeta(c) {
  return CATEGORIES.find((x) => x.value === c) || CATEGORIES[CATEGORIES.length - 1];
}

function TaskCard({ task, tone, onStart, onComplete, onSkip, onArchive, onEdit, onHistory, onNote }) {
  const cat = categoryMeta(task.category);
  const inProgress = task.current_instance_status === "in_progress";
  return (
    <div
      data-testid={`task-card-${task.id}`}
      className={`border-l-4 ${tone.border} border-y border-r border-canvas-line bg-canvas-card p-3 space-y-2`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block px-1.5 py-0.5 text-[9px] font-mono font-semibold uppercase tracking-widest ${cat.color}`}>{cat.label}</span>
            <span className="font-mono text-[9px] text-ink-muted uppercase tracking-widest">{task.frequency.replace("_", " ")}</span>
            {inProgress && (
              <span className="inline-block px-1.5 py-0.5 text-[9px] font-mono font-semibold bg-amber-100 text-amber-800 uppercase tracking-widest">
                In Progress
              </span>
            )}
          </div>
          <div className="font-semibold text-sm mt-1 truncate">{task.title}</div>
          {task.description && (
            <div className="text-xs text-ink-muted mt-0.5 line-clamp-2">{task.description}</div>
          )}
          <div className="font-mono text-[10px] text-ink-muted mt-1 flex items-center gap-1.5">
            <Clock size={11} />
            Due <b>{task.next_due_date}</b>
            {task.assigned_role && <span>· role: {task.assigned_role}</span>}
          </div>
          {(task.assigned_employee_name || task.assigned_employee_phone || task.assigned_employee_email) && (
            <div
              data-testid={`task-assignee-${task.id}`}
              className="mt-1 flex items-center gap-2 flex-wrap font-mono text-[10px] text-ink"
            >
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-canvas-line bg-canvas">
                <UserIcon size={10} weight="bold" />
                {task.assigned_employee_name || "—"}
              </span>
              {task.assigned_employee_phone && (
                <a
                  href={`tel:${task.assigned_employee_phone}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-canvas-line bg-canvas hover:border-ink"
                  data-testid={`task-assignee-phone-${task.id}`}
                >
                  <Phone size={10} weight="bold" />
                  {task.assigned_employee_phone}
                </a>
              )}
              {task.assigned_employee_email && (
                <a
                  href={`mailto:${task.assigned_employee_email}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-canvas-line bg-canvas hover:border-ink"
                  data-testid={`task-assignee-email-${task.id}`}
                >
                  <EnvelopeSimple size={10} weight="bold" />
                  {task.assigned_employee_email}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {!inProgress && (
          <button onClick={() => onStart(task)} data-testid={`task-start-${task.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1">
            <PlayCircle size={11} weight="bold" /> Start
          </button>
        )}
        {inProgress && (
          <button
            onClick={() => onNote(task)}
            data-testid={`task-note-${task.id}`}
            title="Log a progress update without completing"
            className="px-2 py-1 border border-amber-400 bg-amber-50 text-amber-900 text-[10px] font-mono uppercase tracking-wider hover:border-amber-600 flex items-center gap-1"
          >
            <ClipboardText size={11} weight="bold" /> Update
          </button>
        )}
        <button
          onClick={() => inProgress && onComplete(task)}
          data-testid={`task-complete-${task.id}`}
          disabled={!inProgress}
          title={inProgress ? "Mark task as completed" : "Start the task first"}
          className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1 ${
            inProgress
              ? "bg-emerald-700 text-white hover:bg-emerald-800 cursor-pointer"
              : "bg-emerald-700/30 text-white/70 cursor-not-allowed"
          }`}
        >
          <CheckCircle size={11} weight="bold" /> Complete
        </button>
        <button onClick={() => onSkip(task)} data-testid={`task-skip-${task.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1">
          <SkipForward size={11} weight="bold" /> Skip
        </button>
        <button onClick={() => onEdit(task)} data-testid={`task-edit-${task.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink">
          Edit
        </button>
        <button onClick={() => onHistory(task)} data-testid={`task-history-${task.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1">
          <ListChecks size={11} weight="bold" /> History
        </button>
        <button onClick={() => onArchive(task)} data-testid={`task-archive-${task.id}`} className="ml-auto p-1 text-ink-subtle hover:text-destructive" title="Archive">
          <Trash size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function ActionModal({ task, action, onClose, onConfirm }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  if (!task) return null;
  const title = {
    start: "Start Task",
    complete: "Complete Task",
    skip: "Skip Task",
    note: "Add Progress Update",
  }[action];
  const placeholder = {
    start: "Anything to record?",
    complete: "Anything to record?",
    skip: "Reason for skipping…",
    note: "What's the current status? e.g. Called ABC bank, awaiting call-back",
  }[action];
  const noteRequired = action === "note";
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="task-action-modal">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (noteRequired && !notes.trim()) {
            toast.error("Please enter an update note");
            return;
          }
          setSaving(true);
          try { await onConfirm(notes); } finally { setSaving(false); }
        }}
        className="bg-canvas border border-canvas-line max-w-md w-full"
      >
        <div className="border-b border-canvas-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">{title}</div>
            <div className="text-lg font-semibold">{task.title}</div>
            <div className="font-mono text-[10px] text-ink-muted">Due {task.next_due_date}</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-6">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
              {noteRequired ? "Update Note *" : "Notes (optional)"}
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="task-action-notes"
              rows={3}
              className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
              placeholder={placeholder}
              autoFocus={noteRequired}
              required={noteRequired}
            />
          </label>
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line">Cancel</button>
          <button type="submit" disabled={saving} data-testid="task-action-confirm" className="px-4 py-2 bg-ink text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand disabled:opacity-40">
            {saving ? "Saving…" : (action === "note" ? "Log Update" : title.split(" ")[0])}
          </button>
        </div>
      </form>
    </div>
  );
}

function TaskFormModal({ initial, branches, onClose, onSave }) {
  const [form, setForm] = useState(initial || {
    title: "", description: "", category: "compliance", frequency: "monthly",
    next_due_date: new Date().toISOString().slice(0, 10),
    weekly_day: 0, monthly_day: null,
    assigned_user_id: "", assigned_branch_id: "", assigned_role: "",
    assigned_employee_id: "",
    email_notifications: false, advance_notice_days: 3,
  });
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Fetch active employees whenever the assigned branch changes.
  // No branch = fetch every active employee (still capped by backend).
  useEffect(() => {
    let cancelled = false;
    setEmpLoading(true);
    const params = { status: "active" };
    if (form.assigned_branch_id) params.branch_id = form.assigned_branch_id;
    api.get("/hr/employees", { params })
      .then((r) => { if (!cancelled) setEmployees(r.data || []); })
      .catch(() => { if (!cancelled) setEmployees([]); })
      .finally(() => { if (!cancelled) setEmpLoading(false); });
    return () => { cancelled = true; };
  }, [form.assigned_branch_id]);

  // If the picked employee no longer belongs to the selected branch,
  // clear it so we don't ship a stale reference.
  useEffect(() => {
    if (!form.assigned_employee_id) return;
    if (empLoading) return;
    if (!employees.some((e) => e.id === form.assigned_employee_id)) {
      set("assigned_employee_id", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, empLoading]);

  const selectedEmp = employees.find((e) => e.id === form.assigned_employee_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Strip empty assignment strings so the backend treats them as null
      ["assigned_user_id", "assigned_branch_id", "assigned_role", "assigned_employee_id"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };
  const isEdit = !!initial?.id;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="task-form-modal">
      <form onSubmit={submit} className="bg-canvas border border-canvas-line max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">{isEdit ? "Edit" : "New"} Task</div>
            <div className="text-lg font-semibold">Schedule a recurring event or one-off deadline</div>
          </div>
          <button type="button" onClick={onClose} data-testid="task-form-close" className="p-2 border border-canvas-line hover:border-ink"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Title *</span>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-form-title" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" required autoFocus />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Description</span>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="task-form-desc" rows={2} className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="task-form-category" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Frequency</span>
              <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="task-form-frequency" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                {form.frequency === "once" ? "Due Date *" : "First / Next Due Date *"}
              </span>
              <input type="date" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} data-testid="task-form-date" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" required />
            </label>
            {form.frequency === "weekly" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Every</span>
                <select value={form.weekly_day ?? 0} onChange={(e) => set("weekly_day", parseInt(e.target.value, 10))} data-testid="task-form-weekly-day" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </label>
            )}
            {["monthly", "quarterly", "fy_quarterly"].includes(form.frequency) && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Day of Month</span>
                <select value={form.monthly_day ?? ""} onChange={(e) => set("monthly_day", parseInt(e.target.value, 10) || null)} data-testid="task-form-monthly-day" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="">Auto (from date)</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                  <option value={99}>Last day of month</option>
                </select>
              </label>
            )}
          </div>
          <div className="border-t border-canvas-line pt-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Assign To (pick any combination — leave blank for broadcast)</div>
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Branch</span>
                <select value={form.assigned_branch_id || ""} onChange={(e) => set("assigned_branch_id", e.target.value)} data-testid="task-form-branch" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="">— none —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Role</span>
                <select value={form.assigned_role || ""} onChange={(e) => set("assigned_role", e.target.value)} data-testid="task-form-role" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="">— none —</option>
                  <option value="admin">Admin</option>
                  <option value="branch_manager">Branch Manager</option>
                  <option value="accounts">Accounts</option>
                  <option value="hr">HR</option>
                  <option value="sales">Sales</option>
                  <option value="service">Service</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                  Employee {form.assigned_branch_id ? "(branch)" : "(all active)"}
                </span>
                <select
                  value={form.assigned_employee_id || ""}
                  onChange={(e) => set("assigned_employee_id", e.target.value)}
                  data-testid="task-form-employee"
                  className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                >
                  <option value="">
                    {empLoading ? "Loading…" : `— none — (${employees.length})`}
                  </option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {[e.name, e.designation, e.phone].filter(Boolean).join(" · ")}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {selectedEmp && (
              <div
                data-testid="task-form-employee-contact"
                className="border border-canvas-line bg-canvas-panel/50 px-3 py-2 space-y-1"
              >
                <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                  Contact (from HR record)
                </div>
                <div className="flex items-center gap-3 flex-wrap text-[12px] text-ink">
                  <span className="inline-flex items-center gap-1.5">
                    <UserIcon size={12} weight="bold" className="text-ink-muted" />
                    <b>{selectedEmp.name}</b>
                    {selectedEmp.employee_code && (
                      <span className="font-mono text-[10px] text-ink-muted">· {selectedEmp.employee_code}</span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone size={12} weight="bold" className="text-ink-muted" />
                    {selectedEmp.phone ? (
                      <a href={`tel:${selectedEmp.phone}`} className="font-mono hover:underline">{selectedEmp.phone}</a>
                    ) : (
                      <span className="italic text-ink-subtle text-[11px]">no phone on file</span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <EnvelopeSimple size={12} weight="bold" className="text-ink-muted" />
                    {selectedEmp.email ? (
                      <a href={`mailto:${selectedEmp.email}`} className="font-mono hover:underline">{selectedEmp.email}</a>
                    ) : (
                      <span className="italic text-ink-subtle text-[11px]">no email on file</span>
                    )}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-ink-muted flex items-center gap-3 flex-wrap">
                  {selectedEmp.designation && <span>Role · {selectedEmp.designation}</span>}
                  {selectedEmp.work_area && <span>Dept · {selectedEmp.work_area}</span>}
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-canvas-line pt-4 grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.email_notifications} onChange={(e) => set("email_notifications", e.target.checked)} data-testid="task-form-email" />
              <span>Send email reminder (MOCKED)</span>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Advance Notice (days)</span>
              <input type="number" min={0} max={30} value={form.advance_notice_days ?? 3} onChange={(e) => set("advance_notice_days", parseInt(e.target.value, 10) || 0)} data-testid="task-form-advance" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
          </div>
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-end gap-2 sticky bottom-0 bg-canvas">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line">Cancel</button>
          <button type="submit" disabled={saving} data-testid="task-form-save" className="px-4 py-2 bg-ink text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand disabled:opacity-40">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create task")}
          </button>
        </div>
      </form>
    </div>
  );
}


// Outcome → colour tag for the history timeline. Kept in one place so it
// stays consistent between the sidebar dot and the row badge.
const HISTORY_OUTCOME_META = {
  created: { label: "Created", cls: "bg-neutral-200 text-neutral-800" },
  updated: { label: "Updated", cls: "bg-slate-200 text-slate-800" },
  reassigned: { label: "Reassigned", cls: "bg-indigo-100 text-indigo-800" },
  started: { label: "Started", cls: "bg-amber-100 text-amber-800" },
  progress_note: { label: "Update", cls: "bg-amber-50 text-amber-900 border border-amber-300" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800" },
  skipped: { label: "Skipped", cls: "bg-rose-100 text-rose-800" },
  archived: { label: "Archived", cls: "bg-neutral-800 text-white" },
};

function TaskHistoryModal({ task, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get(`/tasks/${task.id}/history`)
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err)); });
    return () => { cancelled = true; };
  }, [task.id]);
  const fmtTs = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch { return iso; }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="task-history-modal">
      <div className="bg-canvas border border-canvas-line max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Activity Timeline</div>
            <div className="text-lg font-semibold truncate">{task.title}</div>
            {task.assigned_employee_name && (
              <div className="font-mono text-[10px] text-ink-muted">
                Assigned to {task.assigned_employee_name}
                {task.assigned_employee_phone ? ` · ${task.assigned_employee_phone}` : ""}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} data-testid="task-history-close" className="p-2 border border-canvas-line hover:border-ink"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-6">
          {rows === null && !error && <div className="text-center font-mono text-xs text-ink-subtle py-8">Loading…</div>}
          {error && <div className="text-center font-mono text-xs text-destructive py-8">{error}</div>}
          {rows && rows.length === 0 && (
            <div className="text-center font-mono text-xs text-ink-subtle py-8">No activity yet.</div>
          )}
          {rows && rows.length > 0 && (
            <ol className="relative border-l-2 border-canvas-line ml-2 pl-5 space-y-4" data-testid="task-history-list">
              {rows.map((r) => {
                const meta = HISTORY_OUTCOME_META[r.outcome] || { label: r.outcome, cls: "bg-neutral-100 text-neutral-700" };
                return (
                  <li key={r.id} className="relative" data-testid={`task-history-row-${r.id}`}>
                    <span className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-canvas border-2 border-ink" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-mono font-semibold uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
                      <span className="font-mono text-[10px] text-ink-muted inline-flex items-center gap-1">
                        <ClockIcon size={10} weight="bold" /> {fmtTs(r.actor_at)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-ink">
                      by <b>{r.actor_email || "—"}</b>
                      {r.due_date && <span className="font-mono text-[11px] text-ink-muted"> · due {r.due_date}</span>}
                    </div>
                    {r.notes && (
                      <div className="mt-1 text-xs text-ink-muted italic">“{r.notes}”</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.permissions || []).includes("branches.view_all");

  const [buckets, setBuckets] = useState({ overdue: [], due_today: [], this_week: [], upcoming: [], paused: [] });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("mine");
  const [category, setCategory] = useState("");
  const [branches, setBranches] = useState([]);
  const [formTask, setFormTask] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [action, setAction] = useState(null); // {task, kind}
  const [historyTask, setHistoryTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view };
      if (category) params.category = category;
      const { data } = await api.get("/tasks", { params });
      setBuckets(data.buckets || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [view, category]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const totals = useMemo(() => ({
    overdue: buckets.overdue?.length || 0,
    due_today: buckets.due_today?.length || 0,
    this_week: buckets.this_week?.length || 0,
    upcoming: buckets.upcoming?.length || 0,
    paused: buckets.paused?.length || 0,
  }), [buckets]);

  const onStart = (task) => setAction({ task, kind: "start" });
  const onComplete = (task) => setAction({ task, kind: "complete" });
  const onSkip = (task) => setAction({ task, kind: "skip" });
  const onNote = (task) => setAction({ task, kind: "note" });
  const onEdit = (task) => { setFormTask(task); setShowForm(true); };
  const onArchive = async (task) => {
    if (!window.confirm(`Archive "${task.title}"? History will be kept.`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Archived");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmAction = async (notes) => {
    if (!action) return;
    const kindToast = {
      start: "started",
      complete: "completed",
      skip: "skipped",
      note: "update logged",
    }[action.kind] || "updated";
    try {
      await api.post(`/tasks/${action.task.id}/${action.kind}`, { notes });
      toast.success(`Task ${kindToast}`);
      setAction(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveForm = async (payload) => {
    try {
      if (formTask?.id) {
        await api.patch(`/tasks/${formTask.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setShowForm(false);
      setFormTask(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const tones = {
    overdue: { border: "border-l-destructive", icon: Warning, iconColor: "text-destructive", label: "Overdue" },
    due_today: { border: "border-l-amber-500", icon: ClockCountdown, iconColor: "text-amber-600", label: "Due Today" },
    this_week: { border: "border-l-sky-500", icon: Clock, iconColor: "text-sky-600", label: "This Week" },
    upcoming: { border: "border-l-ink-muted", icon: ArrowsClockwise, iconColor: "text-ink-muted", label: "Upcoming" },
    paused: { border: "border-l-neutral-300", icon: Pause, iconColor: "text-neutral-500", label: "Paused" },
  };

  const groups = ["overdue", "due_today", "this_week", "upcoming", "paused"];

  return (
    <div>
      <PageHeader
        eyebrow="TASKS / 01"
        title="Task Scheduler"
        subtitle="Recurring compliance events (daily / weekly / monthly / quarterly / yearly / FY) and one-off deadlines. Never miss PF filing, GST returns, salary credit, or an audit."
        actions={
          <button
            onClick={() => { setFormTask(null); setShowForm(true); }}
            data-testid="task-new-btn"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand flex items-center gap-2"
          >
            <Plus size={13} weight="bold" /> New Task
          </button>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="tasks-summary">
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-destructive">Overdue</div>
            <div className="font-display text-2xl font-bold tabular-nums">{totals.overdue}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-amber-600">Due Today</div>
            <div className="font-display text-2xl font-bold tabular-nums">{totals.due_today}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-sky-600">This Week</div>
            <div className="font-display text-2xl font-bold tabular-nums">{totals.this_week}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">Upcoming</div>
            <div className="font-display text-2xl font-bold tabular-nums">{totals.upcoming}</div>
          </div>
          <div className="border border-canvas-line bg-canvas-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Paused</div>
            <div className="font-display text-2xl font-bold tabular-nums">{totals.paused}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="border border-canvas-line bg-canvas-card p-3 flex items-center gap-3 flex-wrap">
          <div className="flex border border-canvas-line">
            <button
              onClick={() => setView("mine")}
              data-testid="tasks-view-mine"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${view === "mine" ? "bg-ink text-white" : "hover:bg-canvas-panel"}`}
            >
              Mine
            </button>
            {isAdmin && (
              <button
                onClick={() => setView("all")}
                data-testid="tasks-view-all"
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-canvas-line ${view === "all" ? "bg-ink text-white" : "hover:bg-canvas-panel"}`}
              >
                All
              </button>
            )}
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="tasks-category-filter"
            className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <div className="ml-auto font-mono text-[10px] text-ink-muted">
            {loading ? "Loading…" : `${Object.values(buckets).reduce((s, b) => s + (b?.length || 0), 0)} tasks`}
          </div>
        </div>

        {/* Buckets */}
        {loading && <div className="text-center font-mono text-xs text-ink-subtle py-12">Loading…</div>}
        {!loading && groups.every((g) => (buckets[g]?.length || 0) === 0) && (
          <div className="border border-canvas-line bg-canvas-card p-12 text-center">
            <ClipboardText size={40} weight="thin" className="mx-auto text-ink-muted" />
            <div className="mt-3 text-sm font-semibold">No tasks yet</div>
            <div className="mt-1 text-xs text-ink-muted">Click &ldquo;New Task&rdquo; to schedule your first recurring compliance event.</div>
          </div>
        )}
        {!loading && groups.map((g) => {
          const items = buckets[g] || [];
          if (items.length === 0) return null;
          const tone = tones[g];
          const Icon = tone.icon;
          return (
            <div key={g} data-testid={`tasks-group-${g}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} weight="bold" className={tone.iconColor} />
                <div className="text-sm font-semibold">{tone.label}</div>
                <div className="font-mono text-[10px] text-ink-muted">({items.length})</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {items.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    tone={tone}
                    onStart={onStart}
                    onComplete={onComplete}
                    onSkip={onSkip}
                    onEdit={onEdit}
                    onArchive={onArchive}
                    onHistory={setHistoryTask}
                    onNote={onNote}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <TaskFormModal
          initial={formTask}
          branches={branches}
          onClose={() => { setShowForm(false); setFormTask(null); }}
          onSave={saveForm}
        />
      )}
      {action && (
        <ActionModal
          task={action.task}
          action={action.kind}
          onClose={() => setAction(null)}
          onConfirm={confirmAction}
        />
      )}
      {historyTask && (
        <TaskHistoryModal
          task={historyTask}
          onClose={() => setHistoryTask(null)}
        />
      )}
    </div>
  );
}
